import { useEffect, useRef } from "react";

import { cn } from "../foundations/cn.js";
import { Button } from "./Button.js";

export interface LoadMoreProps {
  /** How many are on the page now. */
  shown: number;
  /** How many there are in all, when known. */
  total?: number;
  /** Whether another page exists; defaults to `shown < total`. */
  hasMore?: boolean;
  onLoadMore: () => void;
  /** True while the next page is on its way: the button stays put and says so. */
  loading?: boolean;
  /** The button's words: "Load more", "Show more pulls". */
  label?: string;
  /** The count line, announced politely after each page: "Showing 20 of 134". */
  status?: (shown: number, total: number | undefined) => string;
  /**
   * Infinite scroll: load the next page by itself when this row scrolls into view (within
   * `autoLoadMargin` of it), once per page. The button stays, for keyboard users and for a browser
   * without IntersectionObserver.
   */
  autoLoad?: boolean;
  /** How far ahead of the row the next page starts loading, as a CSS margin. */
  autoLoadMargin?: string;
  /** Keeps the count for screen readers only, where a visible count is noise (a popover list). */
  hideStatus?: boolean;
  className?: string;
}

const defaultStatus = (shown: number, total: number | undefined) =>
  total === undefined ? `Showing ${shown}` : `Showing ${shown} of ${total}`;

/**
 * The next page of a list or Table, on request or, with `autoLoad`, as the list's end scrolls
 * into view. The count is a polite live region, so a screen reader hears how many arrived; focus
 * stays on the button, and moves to the count when the last page removes it, so it is never
 * dropped onto the page.
 */
export function LoadMore({
  shown,
  total,
  hasMore: hasMoreProp,
  onLoadMore,
  loading = false,
  label = "Load more",
  status = defaultStatus,
  autoLoad = false,
  autoLoadMargin = "400px 0px",
  hideStatus = false,
  className,
}: LoadMoreProps) {
  const hasMore = hasMoreProp ?? (total !== undefined && shown < total);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const hadFocus = useRef(false);
  const loadRef = useRef(onLoadMore);
  loadRef.current = onLoadMore;

  useEffect(() => {
    // The button just went away under the reader's focus, which the browser drops on the body.
    const dropped = document.activeElement === null || document.activeElement === document.body;
    if (!hasMore && hadFocus.current && dropped) statusRef.current?.focus();
    if (!hasMore) hadFocus.current = false;
  }, [hasMore]);

  // A fresh observer per page: its first report says whether the row is still in view after the
  // last page arrived (a short page), so a list that doesn't fill the screen keeps loading.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `shown` re-arms it once a page lands, for a caller that loads without a loading state.
  useEffect(() => {
    const node = rootRef.current;
    if (!autoLoad || !hasMore || loading || !node) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          loadRef.current();
        }
      },
      { rootMargin: autoLoadMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [autoLoad, hasMore, loading, autoLoadMargin, shown]);

  return (
    <div ref={rootRef} className={cn("flex flex-col items-center gap-2 px-4 py-4", className)}>
      <p
        ref={statusRef}
        role="status"
        tabIndex={-1}
        className={cn(
          "font-body text-body-b3 text-text-tertiary outline-hidden",
          hideStatus && "sr-only",
        )}
      >
        {status(shown, total)}
      </p>
      {hasMore && (
        <Button
          ref={buttonRef}
          variant="secondary"
          size="sm"
          loading={loading}
          onClick={() => {
            hadFocus.current = document.activeElement === buttonRef.current;
            onLoadMore();
          }}
        >
          {label}
        </Button>
      )}
    </div>
  );
}
